// src/components/DynamicSetupWizard/Components/CardPicker.tsx
import React from "react";
import { Row, Col, Card, CardBody, Alert } from "reactstrap";

interface CardPickerProps<T> {
  title: string;
  description: string;
  items: T[];
  idField: keyof T;
  labelField: keyof T;
  logoField?: keyof T;
  loading: boolean;
  error?: string;
  pending: T | null;
  onSelect: (item: T) => void;
}

export default function CardPicker<T extends Record<string, any>>({
  title,
  description,
  items,
  idField,
  labelField,
  logoField,
  loading,
  error,
  pending,
  onSelect
}: CardPickerProps<T>) {
  // shimmer placeholders
  if (loading) {
    const count = 4;
    return (
      <>
        <h5 className="fw-bold">{title}</h5>
        <p className="text-secondary mb-4">{description}</p>
        <Row className="g-3">
          {Array.from({ length: count }).map((_, idx) => (
            <Col key={idx} xs="12" sm="6" md="4" lg="3">
              <Card className="w-100 shadow-sm rounded bg-light border-light p-4">
                <CardBody className="position-relative d-flex flex-column align-items-center p-0 w-100">
                  <div className="position-absolute top-0 end-0 m-2">
                    <span className="placeholder rounded-circle placeholder-lg" />
                  </div>
                  <div className="placeholder-glow my-3 text-center">
                    <span
                      className="placeholder rounded-circle"
                      style={{ width: 64, height: 64, display: "inline-block" }}
                    />
                  </div>
                  <div className="placeholder-glow w-75 mb-1 text-center">
                    <span className="placeholder col-6" />
                  </div>
                  <div className="placeholder-glow w-50 text-center">
                    <span className="placeholder col-4" />
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </>
    );
  }

  return (
    <>
      <h5 className="fw-bold">{title}</h5>
      <p className="text-secondary mb-4">{description}</p>
      {error && <Alert color="danger">{error}</Alert>}
      <Row className="g-3">
        {items.map(item => {
          const id  = String(item[idField]);
          const lbl = String(item[labelField]);
          const sel = pending?.[idField] === item[idField];
          const raw = logoField ? item[logoField] : undefined;
          const logo = typeof raw === "string" && raw.trim() ? raw : undefined;

          return (
            <Col key={id} xs="12" sm="6" md="4" lg="3">
              <Card
                tag="button"
                type="button"
                onClick={() => onSelect(item)}
                className={`w-100 d-flex flex-column align-items-center shadow-sm rounded bg-light ${
                  sel ? "border border-primary p-4" : "border border-light p-4"
                }`}
              >
                <CardBody className="position-relative d-flex flex-column align-items-center p-0 w-100">
                  <div className="position-absolute top-0 end-0 m-2">
                    <i
                      className={`${
                        sel
                          ? "ri-checkbox-circle-fill text-primary fs-2"
                          : "ri-checkbox-circle-line text-secondary fs-2"
                      }`}
                    />
                  </div>
                  {logo ? (
                    <img
                      src={logo}
                      alt={lbl}
                      width={64}
                      height={64}
                      className="rounded-circle my-3"
                    />
                  ) : (
                    <i
                      className="ri-stack-line text-primary my-3"
                      style={{ fontSize: "2.5rem" }}
                    />
                  )}
                  <h6 className="mb-1 text-truncate">{lbl}</h6>
                  <small className="text-muted">{id}</small>
                </CardBody>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
}
